/**
 * Sound Notification Context
 * Provides app-wide sound notification functionality
 * Integrates with WebSocket events for real-time alerts
 */
import React, { createContext, useContext, useEffect, useCallback } from 'react';
import { useSoundNotifications } from '../hooks/useSoundNotifications';
import { useWS } from './WebSocketContext';
import { useAuth } from './AuthContext';

const SoundContext = createContext(null);

export function SoundProvider({ children }) {
  const sound = useSoundNotifications();
  const ws = useWS();
  const { user } = useAuth();

  // Listen for WebSocket events and play appropriate sounds
  useEffect(() => {
    if (!ws || !user) return;

    // Track previous online users to detect changes
    let previousOnlineUsers = new Set();

    // Handle presence changes
    const checkPresenceChanges = () => {
      if (!ws.onlineUsers) return;
      
      const currentOnline = new Set(ws.onlineUsers);
      
      // Find new users who came online
      currentOnline.forEach(userId => {
        if (!previousOnlineUsers.has(userId) && userId !== user.id) {
          sound.playSound('userLogin');
        }
      });
      
      // Find users who went offline
      previousOnlineUsers.forEach(userId => {
        if (!currentOnline.has(userId) && userId !== user.id) {
          sound.playSound('userLogout');
        }
      });
      
      previousOnlineUsers = currentOnline;
    };

    // Set up interval to check presence (WebSocket updates onlineUsers)
    const presenceInterval = setInterval(checkPresenceChanges, 2000);

    return () => {
      clearInterval(presenceInterval);
    };
  }, [ws, user, sound]);

  // Handle new messages from WebSocket
  useEffect(() => {
    if (!ws?.lastMessage || !user) return;

    const message = ws.lastMessage;
    
    // Don't play sound for own messages
    if (message.sender_id === user.id) return;

    // Check if message mentions the current user
    const isMention = message.content?.includes(`@${user.name}`) || 
                      message.content?.includes(`@${user.email}`);

    if (isMention) {
      sound.playSound('mention');
    } else if (message.priority === 'high') {
      sound.playSound('messagePriority');
    } else {
      sound.playSound('message');
    }
  }, [ws?.lastMessage, user, sound]);

  // Expose a method to play sounds from anywhere in the app
  const notify = useCallback((type, options = {}) => {
    switch (type) {
      case 'success':
        sound.playSound('success');
        break;
      case 'warning':
        sound.playSound('warning');
        break;
      case 'error':
        sound.playSound('error');
        break;
      case 'message':
        sound.playSound(options.priority === 'high' ? 'messagePriority' : 'message');
        break;
      case 'mention':
        sound.playSound('mention');
        break;
      case 'userLogin':
        sound.playSound('userLogin');
        break;
      case 'userLogout':
        sound.playSound('userLogout');
        break;
      default:
        sound.playSound('message');
    }
  }, [sound]);

  const value = {
    ...sound,
    notify,
  };

  return (
    <SoundContext.Provider value={value}>
      {children}
    </SoundContext.Provider>
  );
}

export function useSound() {
  const context = useContext(SoundContext);
  if (!context) {
    throw new Error('useSound must be used within a SoundProvider');
  }
  return context;
}

export default SoundContext;
